import { Font, StyleSheet } from '@react-pdf/renderer';

// Constants
import { poppinsFont, montserratFont } from '@/constants/font';

// Utils
import { detectEdgeBrowserOnWindows } from '@shared/utils';

const isEdgeOnWindows = detectEdgeBrowserOnWindows();
const fontResume = isEdgeOnWindows ? montserratFont : poppinsFont;

// Tokens
import theme from '../tokens';

// Register fonts
Font.register(fontResume);

const styles = StyleSheet.create({
  // Page layout
  page: {
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.colors.background,
    width: '100%',
    height: '100%',
    padding: `${theme.spacing.headerHeight} 0 ${theme.spacing.footerHeight}`,
    display: 'flex',
    flexDirection: 'column',
  },

  container: {
    flexDirection: 'row',
    width: '100%',
    minHeight: '100%',
    padding: `0 ${theme.spacing.pagePadding}`,
    gap: theme.spacing.containerGap,
  },

  // Header
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: theme.spacing.headerHeight,
    backgroundColor: theme.colors.background,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: `0 ${theme.spacing.pagePadding}`,
  },

  logo: {
    width: theme.spacing.logoWidth,
    height: theme.spacing.logoHeight,
    margin: 0,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: theme.spacing.footerHeight,
    backgroundColor: theme.colors.background,
    justifyContent: 'flex-end',
  },

  pageNumberWrapper: {
    backgroundColor: theme.colors.primary,
    width: 22,
    height: 30,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-end',
    marginRight: theme.spacing.pagePadding,
  },

  pageNumberText: {
    fontWeight: 600,
    color: theme.colors.background,
    fontSize: theme.typography.fontSize['2xl'],
  },

  borderLastPage: {
    borderBottomWidth: 1,
    borderBottomColor: '#ABABAB',
  },
  // Sidebar
  sidebar: {
    width: theme.spacing.sidebarWidth,
    backgroundColor: theme.colors.background,
  },

  profileContainer: {
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.spacing.cardRadius,
    paddingHorizontal: theme.spacing.cardPaddingX,
    paddingVertical: theme.spacing.cardPaddingY,
  },

  profileSection: {
    alignItems: 'center',
  },

  profileImageContainer: {
    width: theme.spacing.avatarSize,
    height: theme.spacing.avatarSize,
    borderRadius: theme.spacing.avatarSize / 2,
    overflow: 'hidden',
    marginBottom: theme.spacing.large,
  },

  profileImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  name: {
    width: '100%',
    fontSize: theme.typography.fontSize['6xl'],
    fontWeight: 'bold',
    textAlign: 'center',
    lineHeight: 1.2,
    marginBottom: theme.spacing.medium,
    textTransform: 'uppercase',
  },

  title: {
    fontSize: theme.typography.fontSize['4xl'],
    color: theme.colors.primary,
    textAlign: 'center',
  },

  divider: {
    borderBottom: `1px solid ${theme.colors.border}`,
    marginVertical: theme.spacing.large,
    width: '100%',
  },

  // Contact section
  contactList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.medium,
    width: '100%',
    marginVertical: theme.spacing.large,
  },

  contactItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
  },

  contactIcon: {
    width: theme.spacing.iconSize,
    height: theme.spacing.iconSize,
    marginRight: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  contactText: {
    fontSize: theme.typography.fontSize.base,
    color: theme.colors.foreground,
    textDecoration: 'none',
    flex: 1,
  },

  // Social media
  socialLinks: {
    width: '100%',
    marginTop: 0,
    marginBottom: theme.spacing.large,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.socialGap,
  },

  socialItem: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },

  socialIcon: {
    width: theme.spacing.iconSize,
    height: theme.spacing.iconSize,
    marginRight: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.primary,
  },

  socialText: {
    flex: 1,
    fontSize: theme.typography.fontSize.base,
    color: theme.colors.foreground,
    textDecoration: 'underline',
    flexWrap: 'wrap',
    wordBreak: 'break-word',
    width: '100%',
  },

  // Section styling
  sectionContainer: {
    marginTop: theme.spacing.sectionHeadingMargin,
  },

  sectionTitle: {
    fontSize: theme.typography.fontSize['5xl'],
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },

  // Education section
  educationSection: {
    marginBottom: 8,
  },
  educationDegree: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: 'bold',
    marginTop: theme.spacing.medium,
  },

  educationDate: {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.primary,
    marginTop: theme.spacing.small,
  },

  educationSchool: {
    fontSize: theme.typography.fontSize.sm,
    fontWeight: 'semibold',
    marginTop: theme.spacing.small,
    lineHeight: 1.25,
    marginBottom: theme.spacing.schoolAfter,
  },

  schoolLocationContainer: {
    marginTop: theme.spacing.medium,
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },

  schoolLocation: {
    fontSize: theme.typography.fontSize.base,
    width: '100%',
  },

  // Skills section
  skillCategoryTitle: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: 'bold',
    marginTop: theme.spacing.medium,
  },

  skillList: {
    marginTop: theme.spacing.small,
  },

  skillItem: {
    fontSize: theme.typography.fontSize.sm,
    marginTop: theme.spacing.bulletBefore,
    lineHeight: 1.25,
    paddingLeft: theme.spacing.small,
  },

  // Main content
  content: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },

  contentSection: {
    marginBottom: theme.spacing.sectionBottom,
  },

  contentSectionTitle: {
    fontSize: theme.typography.fontSize['5xl'],
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },

  paragraph: {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight,
    textAlign: 'justify',
    marginTop: theme.spacing.small,
  },

  // Company information
  companyInfo: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },

  companyName: {
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: 'bold',
    color: theme.colors.primary,
  },

  companyAddressContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },

  addressIcon: {
    width: theme.spacing.iconSize,
    height: theme.spacing.iconSize,
    marginRight: 6,
    paddingTop: 1,
  },

  companyAddress: {
    fontSize: theme.typography.fontSize.base,
    width: '100%',
  },

  companyDate: {
    fontSize: theme.typography.fontSize.base,
    color: theme.colors.primary,
  },

  // Role information
  roleContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },

  roleLabel: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: 'bold',
    marginRight: 3,
  },

  roleText: {
    fontSize: theme.typography.fontSize.base,
    flex: 1,
  },

  // Project information
  project: {
    marginBottom: 4,
    borderTop: `1px solid ${theme.colors.border}`,
    paddingTop: 5,
  },

  projectHeader: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },

  projectName: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: 'bold',
    color: theme.colors.foreground,
    textTransform: 'uppercase',
  },

  projectDate: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: 'medium',
    color: theme.colors.primary,
  },

  // Tech stack
  techStackLabel: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: 'bold',
    margin: '2 0 2',
  },

  techStackContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 4,
    gap: 2.5,
  },

  techTag: {
    backgroundColor: theme.colors.surface,
    padding: '1.5 4',
    fontSize: theme.typography.fontSize.xs,
    borderRadius: 2,
  },

  responsibilitiesContainer: {
    width: '100%',
    flexWrap: 'wrap',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },

  responsibilitiesLabel: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: 'bold',
    margin: '2 0 2',
  },

  // Certification section
  certificationItem: {
    marginVertical: theme.spacing.large,
  },

  certificationHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },

  certificationLogo: {
    width: 28,
    height: 28,
    marginTop: theme.spacing.small,
    backgroundColor: '#646363',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },

  certificationNameContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 4,
  },

  certificationName: {
    fontSize: theme.typography.fontSize.base,
    color: theme.colors.foreground,
    lineHeight: 1.4,
    fontWeight: 'semibold',
  },

  certificationDate: {
    fontSize: theme.typography.fontSize.base,
    color: theme.colors.primary,
    fontWeight: 'medium',
  },

  certificationMetaContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },

  certificationLink: {
    textDecoration: 'none',
  },
});

export default styles;
